// Hidden developer screen (/settings/developer): point the app at a different
// backend and test connectivity before committing to it. Reached only via the
// unlock in Settings (tap the version footer DEV_UNLOCK_TAPS times) — nothing
// visible in the UI advertises it, and it bounces back to Settings if locked.

import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useT } from '../../i18n/index.js';
import { SettingsSubScreen } from './chrome.js';
import { DiagnosticsSection } from './diagnostics/DiagnosticsSection.js';
import { webDiagnostics, type DiagnosticsGateway } from './diagnostics/gateway.js';
import { withDeadline } from './diagnostics/deadline.js';
import {
  PROBE_TIMEOUT_MS,
  clearDevServerOrigin,
  getDevServerOrigin,
  isDevUnlocked,
  probeServer,
  setDevServerOrigin,
  setDevUnlocked,
  type ProbeResult,
} from '../../shell/devServer.js';

const inputClass = 'min-h-touch rounded-xl border border-edge bg-raised px-3 text-sm text-ink';
const buttonClass =
  'min-h-touch rounded-xl bg-accent px-4 text-sm font-medium text-ink disabled:opacity-40 active:bg-accent-press';
const quietButtonClass = 'min-h-touch rounded-xl bg-raised px-4 text-sm text-ink';

type ProbeState = ProbeResult | { status: 'testing' };

function ProbeStatus({ probe }: { probe: ProbeState }): ReactNode {
  const t = useT();
  if (probe.status === 'testing') {
    return (
      <p role="status" className="text-sm text-muted">
        {t('developer.testing')}
      </p>
    );
  }
  if (probe.status === 'ok') {
    return (
      <p role="status" className="text-sm text-ink">
        {t('developer.reachable', { ms: probe.latencyMs })}
      </p>
    );
  }
  const message =
    probe.status === 'http-error'
      ? t('developer.httpError', { status: probe.httpStatus })
      : {
          'cors-blocked': t('developer.corsBlocked'),
          unreachable: t('developer.unreachable'),
          timeout: t('developer.timeout', { secs: PROBE_TIMEOUT_MS / 1000 }),
          invalid: t('developer.invalid'),
        }[probe.status];
  return (
    <p role="alert" className="text-sm text-danger">
      {message}
    </p>
  );
}

export function DeveloperScreen({
  fetchFn,
  diagnostics = webDiagnostics,
}: {
  /** Test seam; defaults to the real global fetch. */
  fetchFn?: typeof fetch;
  /** Test seam; defaults to the brain's own diagnostics centre. */
  diagnostics?: DiagnosticsGateway;
} = {}): ReactNode {
  const t = useT();
  const [value, setValue] = useState(() => getDevServerOrigin() ?? '');
  const [probe, setProbe] = useState<ProbeState | null>(null);
  // Ignore results of probes that were superseded by a newer click.
  const probeSeq = useRef(0);
  const active = getDevServerOrigin();

  if (!isDevUnlocked()) return <Navigate to="/settings" replace />;

  async function test(): Promise<void> {
    const seq = ++probeSeq.current;
    setProbe({ status: 'testing' });
    const result = await probeServer(value, fetchFn);
    if (seq === probeSeq.current) setProbe(result);
  }

  function save(e: FormEvent): void {
    e.preventDefault();
    const origin = setDevServerOrigin(value);
    if (!origin) {
      setProbe({ status: 'invalid' });
      return;
    }
    location.reload();
  }

  async function reset(): Promise<void> {
    clearDevServerOrigin();
    setDevUnlocked(false);
    // Leaving dev mode clears the disclosure in the brain's consent record, so
    // a later unlock discloses again (docs/10 §4). Awaited before the reload —
    // the KV write has to land — but under a deadline: this button is the
    // escape hatch out of a dev-server override that broke the app, and
    // storage that never answers must not cost the user their reload.
    await withDeadline(diagnostics.setDevMode(false));
    location.reload();
  }

  return (
    <SettingsSubScreen section="developer" title={t('developer.title')}>
      <section aria-label={t('developer.backendOverride')} className="flex flex-col gap-3 rounded-xl bg-surface p-4">
        <p className="text-xs text-muted">
          {t('developer.overrideHelp1')}
          <span className="font-mono">http://192.168.1.5:8080</span>
          {t('developer.overrideHelp2')}
        </p>
        {active && (
          <p className="text-xs text-muted">
            {t('developer.activeOverride')}{' '}
            <span data-testid="dev-server-active" className="font-mono text-ink">
              {active}
            </span>
          </p>
        )}
        <form onSubmit={save} className="flex flex-col gap-2">
          <input
            className={inputClass}
            inputMode="url"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder={t('developer.placeholder')}
            aria-label={t('developer.serverAddressAria')}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          {probe && <ProbeStatus probe={probe} />}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={quietButtonClass}
              disabled={probe?.status === 'testing'}
              onClick={() => void test()}
            >
              {t('developer.test')}
            </button>
            <button type="submit" className={buttonClass}>
              {t('developer.saveReload')}
            </button>
            <button type="button" className={quietButtonClass} onClick={() => void reset()}>
              {t('developer.resetDefault')}
            </button>
          </div>
        </form>
      </section>
      <DiagnosticsSection diagnostics={diagnostics} />
    </SettingsSubScreen>
  );
}
